import { Box, CircularProgress, Container, Grid, Typography } from '@mui/material';
import React, { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_PRO } from '@gql/pros';
import { useGetJobOfferTemplate } from '../hooks/useGetJobOfferTemplate';
import { useSaveJobOfferTemplate } from '../hooks/useSaveJobOfferTemplate';
import JobOfferAbout from '@modules/JobOffers/JobOfferDetail/components/JobOfferAbout';
import JobOfferBenefits from '@modules/JobOffers/JobOfferDetail/components/JobOfferBenefits';
import JobOfferMissions from '@modules/JobOffers/JobOfferDetail/components/JobOfferMissions';
import JobOfferTitle from '@modules/JobOffers/JobOfferDetail/components/JobOfferProfile';
import JobOfferRecruitmentProcess from '@modules/JobOffers/JobOfferDetail/components/JobOfferRecruitmentProcess';
import JobOfferWantedProfile from '@modules/JobOffers/JobOfferDetail/components/JobOfferWantedProfile';
import JobOfferWhyChooseUs from '@modules/JobOffers/JobOfferDetail/components/JobOfferWhyChooseUs';

const JobOfferTemplateDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();

    const { template, loading: fetchingTemplate, error: templateError } = useGetJobOfferTemplate(slug);

    const { data: proData, loading: fetchingPro, error: proError } = useQuery(GET_PRO, {
        variables: { proId: template?.pro?._id },
        skip: !template?.pro?._id,
    });

    const pro = proData?.getPro;

    const [isWantedProfileEditing, setIsWantedProfileEditing] = useState(false);
    const [isBenefitsEditing, setIsBenefitsEditing] = useState(false);
    const isAnyEditing = isWantedProfileEditing || isBenefitsEditing;

    const { submit, loading: saveLoading, methods } = useSaveJobOfferTemplate({
        proId: template?.pro?._id,
        template,
    });

    if (fetchingTemplate || fetchingPro) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                <CircularProgress />
            </Container>
        );
    }

    if (templateError || proError || (!fetchingTemplate && !template)) {
        return (
            <Container>
                <Typography color="error">
                    {templateError?.message ?? proError?.message ?? 'Modèle introuvable.'}
                </Typography>
            </Container>
        );
    }

    return (
        <FormProvider {...methods}>
            <Grid container spacing={2} position="relative">
                <Grid item xs={12} md={8}>
                    <Box display="flex" flexDirection="column" gap="16px" width="100%">
                        <JobOfferTitle
                            pro={pro}
                            jobOffer={template}
                            onSubmit={() => methods.handleSubmit(submit)()}
                            loading={saveLoading}
                        />
                        <JobOfferMissions
                            pro={pro}
                            jobOffer={template}
                            onSubmit={() => methods.handleSubmit(submit)()}
                            loading={saveLoading}
                        />
                         <Box display="flex" 
                            flexDirection={{
                                xs: 'column',
                                md: isAnyEditing ? 'column' : 'row'
                            }}
                            gap="16px"
                            width="100%"
                            alignItems="stretch"
                        >
                            <Box flex={1} display="flex" sx={{ '& > *': { flex: 1, width: '100%' } }}>
                                <JobOfferWantedProfile
                                    pro={pro}
                                    jobOffer={template}
                                    onSubmit={() => methods.handleSubmit(submit)()}
                                    loading={saveLoading}
                                    onEditStateChange={setIsWantedProfileEditing}
                                />
                            </Box>
                            <Box flex={1} display="flex" sx={{ '& > *': { flex: 1, width: '100%' } }}>
                                <JobOfferBenefits
                                    pro={pro}
                                    jobOffer={template}
                                    onSubmit={() => methods.handleSubmit(submit)()}
                                    loading={saveLoading}
                                    onEditStateChange={setIsBenefitsEditing}
                                />
                            </Box>
                        </Box>
                        <JobOfferWhyChooseUs
                            pro={pro}
                            jobOffer={template}
                            onSubmit={() => methods.handleSubmit(submit)()}
                            loading={saveLoading}
                        />
                        <JobOfferRecruitmentProcess
                            pro={pro}
                            jobOffer={template}
                            onSubmit={() => methods.handleSubmit(submit)()}
                            loading={saveLoading}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box display="flex" flexDirection="column" gap="16px" width="100%">
                        <JobOfferAbout
                            pro={pro}
                            jobOffer={template}
                            onSubmit={() => methods.handleSubmit(submit)()}
                            loading={saveLoading}
                        />
                    </Box>
                </Grid>
            </Grid>
        </FormProvider>
    );
};

export default JobOfferTemplateDetail;
